import { Contact, LienExterne, Partie } from './Morceaux';

/**
 * « Confidentialité » (chantier comptes, ticket 05). Chaque phrase a été vérifiée sur le code et
 * sur le serveur le 26 septembre 2026, celles des sessions, des clés d'accès et du changement
 * d'e-mail le 11 octobre 2026 (chantier securite-des-comptes, ticket 05), celles du connecteur de
 * claude.ai le 22 octobre 2026 (chantier connecteur-claude, ticket 04), celles des notifications le 23 octobre
 * 2026 (chantier notifications-socle, tickets 01 et 02 ; Microsoft pour Edge, chantier notifications-sortes,
 * ticket 04), celles de l'historique des gestes le 24 octobre 2026 (chantier journal-des-gestes, tickets 01, 04 et 05) ; le registre qui les porte, avec leurs sources, est
 * `docs/rgpd.md`. Une phrase qui change de vérité se corrige ici ET là-bas, dans le même commit.
 */
export function Confidentialite() {
  return (
    <>
      <p className="page-publique-chapeau">
        Nextilt est un site personnel, non professionnel, ouvert à quelques amis sur invitation. Cette page dit ce
        qu’il garde sur toi, pourquoi, où, combien de temps, et comment tout récupérer ou tout effacer.
      </p>
      <p className="page-publique-date">Mise à jour le 24 octobre 2026.</p>

      <Partie titre="Qui s’en occupe">
        <p>
          Nextilt est tenu par un particulier, à titre non professionnel&nbsp;: la personne qui distribue les codes
          d’invitation. C’est elle qui répond de tes données.
        </p>
        <Contact />
      </Partie>

      <Partie titre="Ce qui est gardé">
        <ul>
          <li>
            <strong>Ton adresse e-mail</strong>, rangée en minuscules, et la date où tu l’as confirmée. C’est ton
            identifiant de connexion, et l’adresse où Nextilt t’écrit pour deux choses seulement&nbsp;: confirmer
            qu’elle est à toi, et t’envoyer un lien si tu oublies ton mot de passe (ou te prévenir qu’on a essayé
            de créer un compte avec elle). Jamais de lettre d’information ni de publicité.
          </li>
          <li>
            <strong>Les liens envoyés par mail</strong>&nbsp;: une empreinte qui les reconnaît, jamais le lien
            lui-même, avec leur date et leur fin (48 heures pour confirmer ton adresse, une heure pour un nouveau
            mot de passe). Un lien qui a servi, ou qu’un plus récent remplace, est effacé.
          </li>
          <li>
            <strong>Ton mot de passe, seulement sous une forme hachée</strong> (argon2id)&nbsp;: personne ne peut le
            relire, pas même l’administrateur.
          </li>
          <li>
            <strong>Tout ce que tu écris dans Nextilt</strong>&nbsp;: tes dossiers et leurs bulles, avec leurs notes,
            dates, délais, lieux, liens et contacts (si tu y notes le nom ou le téléphone de quelqu’un d’autre, ne
            garde que le nécessaire), l’importance que tu règles pour une bulle et la date de ce réglage, la date
            où tu l’as validée, sa difficulté quand un modèle l’a posée (rapide, long…), les liens entre tes
            bulles, et les post-it et listes que tu poses sur leurs cartes.
          </li>
          <li>
            <strong>Ton profil</strong>, si tu le remplis&nbsp;: ton prénom, ta ville et ton code postal ou ton
            quartier, ce que tu fais et ce vers quoi tu vas, ton permis et ta voiture, quand tu as du temps et ton
            énergie en semaine, les lignes que tu y ajoutes, et la date où tu as vu le premier formulaire. Tout est
            facultatif, tu le corriges ou l’effaces dans Profil. Tes contacts n’y sont pas recopiés&nbsp;: Profil
            les relit dans les fiches de tes bulles. Nextilt ne s’en sert pour aucun calcul.
          </li>
          <li>
            <strong>Les journées que tu prépares</strong>, dans l’Agenda ou dans «&nbsp;Maintenant&nbsp;»&nbsp;: le
            jour, l’heure de début, le temps que tu te donnes, si tu bouges ou non, si tu as confirmé la veille que
            la journée tient, et les bulles prévues, avec leur place, leur heure et leur durée.
          </li>
          <li>
            <strong>Où tu en es du tuto.</strong>
          </li>
          <li>
            <strong>Tes sessions ouvertes</strong>&nbsp;: pour chaque connexion, le navigateur et le système de
            l’appareil (par exemple «&nbsp;Chrome, Android&nbsp;»), sa date et ta dernière activité (à l’heure
            près), jamais ton adresse IP. Une session est effacée quand tu sors sur cet appareil&nbsp;; au bout de
            trente jours elle ne vaut plus, et elle est effacée à ta connexion suivante. Tu les vois et tu en
            coupes une dans Paramètres, onglet Compte.
          </li>
          <li>
            <strong>Les clés d’accès</strong>, si l’administrateur t’en a créé une&nbsp;: une clé laisse un programme
            (par exemple Claude, sur ton ordinateur) lire et écrire dans ton compte à ta place. Nextilt en garde le
            nom, les dates de création, de dernière utilisation et de révocation, et une empreinte qui la
            reconnaît, jamais la clé elle-même&nbsp;; tu les vois et tu en révoques une dans Paramètres, onglet IA
            branchée&nbsp;: une clé révoquée n’ouvre plus rien.
          </li>
          <li>
            <strong>Les connexions de Claude</strong>, si tu ajoutes Nextilt dans les connecteurs de claude.ai et
            que tu autorises l’accès&nbsp;: une connexion laisse Claude lire et écrire dans ton compte à ta place,
            jamais supprimer une bulle. Nextilt en garde le nom du client (par exemple «&nbsp;claude.ai&nbsp;»), la
            date d’ouverture, la dernière utilisation (à l’heure près) et le dernier renouvellement, et une
            empreinte de chacun de ses jetons, jamais les jetons eux-mêmes. Une connexion que Claude n’a pas
            renouvelée depuis sept jours ne vaut plus&nbsp;; elle est effacée quand Claude essaie encore de s’en
            servir ou quand une autre connexion s’ouvre. Pendant l’autorisation, la demande et le code qui la
            conclut ne vivent que quelques minutes. Tu vois tes connexions et tu en coupes une dans Paramètres,
            onglet IA branchée&nbsp;: coupée, elle est effacée, et Claude n’a plus accès, tout de suite.
          </li>
          <li>
            <strong>Les téléphones abonnés aux notifications</strong>, si tu les allumes sur un téléphone&nbsp;:
            l’adresse que ton navigateur donne pour joindre ce téléphone et les deux clés qui chiffrent les
            notifications pour lui seul, le navigateur et le système de l’appareil (par exemple «&nbsp;Chrome,
            Android&nbsp;»), le fuseau horaire du téléphone, pour te prévenir à ton heure, la date de l’abonnement
            et la dernière ouverture de l’app sur ce téléphone. C’est gardé tant que le téléphone est abonné&nbsp;:
            tout part quand tu éteins les notifications, quand le téléphone ne les accepte plus, ou si quelqu’un
            d’autre abonne le même navigateur à son compte. Tes réglages des notifications (chaque sorte allumée
            ou non, l’heure des rappels du jour, les heures de ta nuit) sont gardés pour ton compte. Pour chaque notification envoyée, la bulle
            qu’elle annonce, sa sorte, l’heure ou le jour qu’elle annonce et l’heure de l’envoi, pour ne jamais
            te l’envoyer deux fois&nbsp;: c’est gardé trente jours après l’envoi, puis effacé, et effacé plus tôt
            avec le téléphone abonné ou avec la bulle.
          </li>
          <li>
            <strong>L’historique de tes gestes</strong>&nbsp;: quoi (par exemple «&nbsp;créer une bulle&nbsp;»), quand,
            sur quelle bulle et dans quel projet, et qui l’a fait, toi ou Claude quand tu l’as branché. Pour tes
            gestes dans l’app, depuis quel appareil (ordinateur ou téléphone), sous quel angle (Carte, Liste,
            Hiérarchie, Maintenant, Agenda) et, depuis Maintenant, le courage choisi&nbsp;; et quand tu ouvres ou
            fermes un projet ou une fiche, et quand tu changes d’angle. Pour Claude, ce qu’il lit dans Nextilt
            (une bulle, tes projets, Maintenant, une journée…) et l’outil qu’il emploie, jamais ce qu’il cherche.
            Jamais tes textes&nbsp;: ni titre, ni notes, ni lieu, ni contact, ni lien, seulement, quand tu modifies
            une bulle, le nom d’un texte touché. C’est gardé tant que ton compte existe, même pour une bulle que tu as
            supprimée&nbsp;; il sort dans ton export et part avec ton compte. Il servira un jour à te montrer ce
            que tu avais prévu et ce qui s’est passé&nbsp;; aucun écran ne le montre aujourd’hui.
          </li>
          <li>
            <strong>Des dates</strong>&nbsp;: celle de la création de ton compte et de ce que tu y crées, et la
            dernière modification de chaque bulle.
          </li>
        </ul>
        <p>
          Le code d’invitation n’est pas lié à ton compte&nbsp;: rien ne garde qui l’a tapé.
        </p>
        <p>
          <strong>Ton adresse IP</strong> n’est pas rangée avec tes données. Le serveur la garde en mémoire, jamais
          sur disque, pour limiter les essais de connexion et d’inscription (huit en cinq minutes) et le nombre de
          requêtes&nbsp;; elle s’efface quand le serveur redémarre, à chaque mise à jour. De la même façon, en
          mémoire seulement, le serveur compte les mails envoyés à une adresse, pour ne pas en envoyer plus de
          trois par heure. Le journal de l’app ne note ni adresse IP ni ce que tu écris. Le serveur web qui
          reçoit les visites ne tient pas de journal des visites&nbsp;; son journal d’erreurs peut, rarement, noter
          l’adresse d’une connexion qui a échoué, et il est gardé sans durée fixe.
        </p>
      </Partie>

      <Partie titre="Dans ton navigateur">
        <p>
          <strong>Un seul cookie</strong>, «&nbsp;session&nbsp;», qui te garde connecté trente jours au plus. Il est
          strictement nécessaire au service&nbsp;: pas de bandeau à accepter, parce qu’il n’y a rien d’autre. Aucun
          cookie de mesure d’audience ni de publicité.
        </p>
        <p>
          Une idée ajoutée sans réseau attend dans ton navigateur jusqu’à son envoi. Elle est effacée quand tu
          sors, et celle d’un autre compte est jetée si quelqu’un d’autre se connecte sur le même appareil. Le
          navigateur garde aussi les fichiers de l’app pour l’ouvrir plus vite, jamais tes données.
        </p>
      </Partie>

      <Partie titre="Pourquoi">
        <p>
          Pour faire marcher le service que tu as demandé en créant ton compte, et pour rien d’autre (base
          légale&nbsp;: l’exécution du service, article 6.1.b du RGPD). Pas de publicité, pas de revente, pas de
          mesure d’audience. Aucune IA ne lit tes données sur le serveur. Quand tu utilises «&nbsp;Importer un
          plan&nbsp;», c’est toi qui copies l’invite dans l’IA de ton choix&nbsp;: Nextilt ne lui envoie rien.
        </p>
        <p>
          Si tu branches Claude à ton Nextilt, par le connecteur de claude.ai ou par une clé d’accès, c’est toi
          qui le décides&nbsp;: ce que Claude lit de tes projets et de ton Profil pour te répondre part dans ta conversation, chez
          Anthropic, l’entreprise américaine qui fait Claude, et y suit les règles de ton propre compte Claude.
          Nextilt ne lui donne que ce que Claude demande pendant la conversation. Tu coupes cet accès quand tu
          veux, dans Paramètres, onglet IA branchée.
        </p>
      </Partie>

      <Partie titre="Où">
        <p>
          Sur un serveur loué à l’hébergeur Hostinger, situé en France (voir les mentions légales), et dans les
          sauvegardes décrites plus bas. Pour les mails, ton adresse et le contenu du mail passent par Brevo, un
          service d’envoi français dont les données restent dans l’Union européenne&nbsp;: il les achemine pour
          Nextilt et rien d’autre, sans suivre les ouvertures ni les clics. À part ces deux-là, Nextilt ne
          confie aucune donnée à quelqu’un d’autre. Si tu as branché Claude, ce qu’il lit part chez Anthropic, à
          ta demande (voir «&nbsp;Pourquoi&nbsp;»).
        </p>
        <p>
          Si tu reçois les notifications sur un téléphone, chacune passe par le service de notification de ce
          téléphone, celui que ton navigateur utilise&nbsp;: Google pour Android et Chrome, Apple pour l’iPhone,
          Mozilla pour Firefox, Microsoft pour Edge. Elle part chiffrée pour ton téléphone seul&nbsp;: ce service la transmet sans
          pouvoir la lire, il voit seulement qu’un message part vers ton téléphone, et quand. Ces entreprises sont
          souvent américaines.
        </p>
      </Partie>

      <Partie titre="Combien de temps">
        <ul>
          <li>
            <strong>Tant que ton compte existe.</strong> Quand il est supprimé, tout ce qu’il contient part de la
            base aussitôt.
          </li>
          <li>
            <strong>Les sauvegardes sur le serveur</strong>&nbsp;: une copie de toute la base chaque nuit, et avant
            chaque mise à jour et chaque suppression de compte faite par l’administrateur. Chacune est gardée quatorze jours, puis effacée.
          </li>
          <li>
            <strong>Les sauvegardes de l’hébergeur</strong>&nbsp;: Hostinger copie tout le serveur chaque semaine
            et garde les deux plus récentes copies.
          </li>
          <li>
            <strong>Une copie des sauvegardes sur l’ordinateur de l’administrateur</strong>, pour ne rien perdre si
            le serveur tombe. Aujourd’hui, rien ne les efface&nbsp;: elles y restent sans limite de durée, et une
            durée reste à fixer.
          </li>
          <li>
            Quand la base du serveur est remplacée (une restauration, un transfert), l’ancienne est gardée à part
            sur le serveur, sans durée fixe.
          </li>
          <li>
            Si tu demandes tes données, le fichier préparé pour toi reste sur l’ordinateur de l’administrateur
            jusqu’à ce qu’il l’efface, après te l’avoir envoyé.
          </li>
        </ul>
        <p>Après la suppression d’un compte, ses données restent donc dans ces copies tant qu’elles existent.</p>
      </Partie>

      <Partie titre="Qui peut voir tes données">
        <p>
          Aucun autre compte&nbsp;: chaque compte ne voit que ses dossiers. Mais l’administrateur du serveur peut
          techniquement lire la base et ses sauvegardes, comme tout administrateur d’un site.
        </p>
      </Partie>

      <Partie titre="Tes droits, et comment les exercer">
        <p>
          Tu peux demander à <strong>accéder</strong> à tes données, les <strong>corriger</strong>, les{' '}
          <strong>effacer</strong>, les <strong>récupérer</strong> dans un fichier lisible (portabilité), t’
          <strong>opposer</strong> à leur usage ou en demander la <strong>limitation</strong>.
        </p>
        <ul>
          <li>Ce que tu as écrit dans Nextilt, tu le corriges ou l’effaces toi-même, dans l’app.</li>
          <li>
            Pour recevoir toutes tes données, sessions, connexions de Claude, téléphones abonnés aux notifications,
            notifications envoyées et historique de tes gestes compris, dans un fichier lisible (JSON)&nbsp;: Paramètres,
            onglet Mes données, «&nbsp;Exporter tout&nbsp;».
          </li>
          <li>
            Pour supprimer ton compte avec tout ce qu’il contient&nbsp;: Paramètres, onglet Mes données,
            «&nbsp;Supprimer mon compte&nbsp;». Un mail part à ton adresse, et son lien, valable une heure, te
            demande une dernière confirmation. Tu peux aussi écrire, comme indiqué ci-dessous.
          </li>
          <li>
            Pour changer ton adresse e-mail, écris, comme indiqué ci-dessous&nbsp;: l’administrateur la change, et
            ton compte garde tout ce qu’il contient.
          </li>
        </ul>
        <Contact />
        <p>
          La réponse arrive dans un délai d’un mois. Si elle ne te convient pas, tu peux adresser une réclamation à
          la CNIL&nbsp;: <LienExterne href="https://www.cnil.fr/fr/plaintes">cnil.fr/fr/plaintes</LienExterne>.
        </p>
      </Partie>

      <Partie titre="Sécurité">
        <p>
          Tout passe en https, et ton mot de passe n’est gardé que haché. Les données de chaque compte sont séparées
          des autres, et des tests automatiques vérifient qu’un compte ne voit rien d’un autre.
        </p>
      </Partie>
    </>
  );
}
